import Button from '@components/common/Button/Button.component'
import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import { Modal } from 'react-bootstrap'
import { editDeleteDDComponent } from './History.component'
import FilesUploadModal from '@components/pages/Files/FilesUploadModal/FilesUploadModal.component'
import FilesDeleteConfirmModal from '@components/pages/Files/FilesDeleteConfirmModal.component'
import { TooltipComponent } from '@syncfusion/ej2-react-popups'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import {
  deleteFileAction,
  fetchWorkspaceSnapshot,
  renameFileAction,
  uploadFilesAction,
} from '@store/actions/folder.action'
import type { IWorkspace } from '@dataTypes/folder.types'
import { getMockWorkspaceSnapshot } from '@assets/mockdata/PatientTray/mockFilesData'
import { shouldUsePatientTrayMockData } from '@utils/patientTrayMocks.util'
import { ReactComponent as FilesOverviewIcon } from '@assets/images/Sidepanel/files-overview.svg'
import { ReactComponent as FilesUploadIcon } from '@assets/images/Sidepanel/files-upload.svg'
import { ReactComponent as FilesFolderIcon } from '@assets/images/Sidepanel/files-folder.svg'
import { ReactComponent as FilesDocIcon } from '@assets/images/Sidepanel/files-doc.svg'
import { ReactComponent as FilesPdfIcon } from '@assets/images/Sidepanel/files-pdf.svg'
import { ReactComponent as FilesBackIcon } from '@assets/images/Sidepanel/files-back.svg'
import moment from 'moment'
import {
  SkeletonBar,
  SkeletonCard,
  SkeletonList,
  SkeletonRow,
} from './SidePanelSkeleton'

type FileData = {
  id: string
  name: string
  modified: string
  size: string
  isFolder: boolean
}

type FileUpload = {
  name: string
  size: number
  progress: number
  uploaded: boolean
  lastUpdated: string
  file?: File
}

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 bytes'
  const k = 1024
  const sizes = ['bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
}

const formatFolderDisplayName = (name: string): string => {
  if (!name.trim()) return name
  return name
    .split(/\s+/)
    .map((word, wordIndex) =>
      word
        .split('-')
        .map((part, partIndex) => {
          const lower = part.toLowerCase()
          if (wordIndex === 0 && partIndex === 0) {
            return lower.charAt(0).toUpperCase() + lower.slice(1)
          }
          return lower
        })
        .join('-'),
    )
    .join(' ')
}

const FilesTab: React.FC = () => {
  const dispatch = useAppDispatch()
  const selectedPatient = useAppSelector(
    (state) => state.patient.selectedPatient,
  )
  const filesWorkspaceRefreshKey = useAppSelector(
    (state) => state.folder.filesWorkspaceRefreshKey,
  )
  const [workspace, setWorkspace] = useState<IWorkspace | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [fileToDelete, setFileToDelete] = useState<FileData | null>(null)
  const [showRenameModal, setShowRenameModal] = useState(false)
  const [fileToRename, setFileToRename] = useState<FileData | null>(null)
  const [newFileName, setNewFileName] = useState<string>('')
  const renameInputRef = useRef<HTMLInputElement>(null)
  const prevPatientIdRef = useRef<string | null>(null)

  const loadWorkspace = useCallback(
    async (patientId: string, folderId: string | null): Promise<void> => {
      if (shouldUsePatientTrayMockData(patientId)) {
        setWorkspace(getMockWorkspaceSnapshot(patientId, folderId))
        setIsLoading(false)
        return
      }

      setIsLoading(true)
      const result = await dispatch(
        fetchWorkspaceSnapshot({
          patient_id: patientId,
          folder_id: folderId ?? 'root',
          page: 1,
          limit: 100,
        }),
      )
      if (fetchWorkspaceSnapshot.fulfilled.match(result)) {
        setWorkspace(result.payload)
      } else {
        setWorkspace(null)
      }
      setIsLoading(false)
    },
    [dispatch],
  )

  useEffect(() => {
    const patientId = selectedPatient?.patient_id
    if (!patientId) {
      prevPatientIdRef.current = null
      setWorkspace(null)
      setCurrentFolderId(null)
      return
    }

    const patientChanged = prevPatientIdRef.current !== patientId
    prevPatientIdRef.current = patientId

    if (patientChanged) {
      setCurrentFolderId(null)
      void loadWorkspace(patientId, null)
      return
    }

    void loadWorkspace(patientId, currentFolderId)
  }, [selectedPatient?.patient_id, currentFolderId, loadWorkspace])

  useEffect(() => {
    if (filesWorkspaceRefreshKey === 0) return
    const patientId = selectedPatient?.patient_id
    if (!patientId) return
    const id = window.setTimeout(() => {
      void loadWorkspace(patientId, currentFolderId)
    }, 100)
    return () => clearTimeout(id)
  }, [
    filesWorkspaceRefreshKey,
    selectedPatient?.patient_id,
    currentFolderId,
    loadWorkspace,
  ])

  const handleUploadClick = (): void => {
    setShowUploadModal(true)
  }

  const handleCloseUploadModal = (): void => {
    setShowUploadModal(false)
  }

  const handleDeleteFile = (item: FileData): void => {
    setFileToDelete(item)
    setShowDeleteModal(true)
  }

  const handleEditFile = (item: FileData): void => {
    setFileToRename(item)
    setNewFileName(item.name)
    setShowRenameModal(true)
  }

  const handleRenameFile = async (): Promise<void> => {
    if (!fileToRename || !newFileName.trim()) return
    let finalFileName = newFileName.trim()
    if (fileToRename.name.includes('.')) {
      const extension = fileToRename.name.substring(
        fileToRename.name.lastIndexOf('.'),
      )
      if (!finalFileName.endsWith(extension)) {
        finalFileName += extension
      }
    }
    if (finalFileName === fileToRename.name) {
      setShowRenameModal(false)
      setFileToRename(null)
      setNewFileName('')
      return
    }

    const patientId = selectedPatient?.patient_id
    if (shouldUsePatientTrayMockData(patientId) && workspace) {
      setWorkspace({
        ...workspace,
        files: workspace.files.map((file) =>
          file.file_id === fileToRename.id
            ? {
                ...file,
                file_name: finalFileName,
                original_name: finalFileName,
              }
            : file,
        ),
      })
      setShowRenameModal(false)
      setFileToRename(null)
      setNewFileName('')
      return
    }

    await dispatch(
      renameFileAction({ fileId: fileToRename.id, fileName: finalFileName }),
    )
    setShowRenameModal(false)
    setFileToRename(null)
    setNewFileName('')
  }

  useEffect(() => {
    if (showRenameModal && renameInputRef.current && fileToRename?.name) {
      const nameWithoutExt = fileToRename.name.includes('.')
        ? fileToRename.name.substring(0, fileToRename.name.lastIndexOf('.'))
        : fileToRename.name
      setNewFileName(nameWithoutExt)
      setTimeout(() => {
        renameInputRef.current?.focus()
        renameInputRef.current?.select()
      }, 100)
    }
  }, [showRenameModal, fileToRename])

  const confirmDeleteFile = async (): Promise<void> => {
    if (!fileToDelete) return

    const patientId = selectedPatient?.patient_id
    if (shouldUsePatientTrayMockData(patientId) && workspace) {
      setWorkspace({
        ...workspace,
        files: workspace.files.filter(
          (file) => file.file_id !== fileToDelete.id,
        ),
      })
      setFileToDelete(null)
      setShowDeleteModal(false)
      return
    }

    await dispatch(deleteFileAction(fileToDelete.id))
    setFileToDelete(null)
    setShowDeleteModal(false)
  }

  const currentItems: FileData[] = useMemo(() => {
    if (!workspace) return []
    const folders: FileData[] = workspace.folders.map((folder) => ({
      id: folder.folder_id,
      name: formatFolderDisplayName(folder.folder_name),
      modified: folder.date_time_updated_at || '',
      size: formatFileSize(folder.folder_size || 0),
      isFolder: true,
    }))
    const files: FileData[] = workspace.files.map((file) => ({
      id: file.file_id,
      name: file.file_name || file.original_name,
      modified: file.date_time_created_at || '',
      size: formatFileSize(file.file_size || 0),
      isFolder: false,
    }))
    return [...folders, ...files]
  }, [workspace])

  const handleFolderClick = (folderId: string): void => {
    setCurrentFolderId(folderId)
  }

  const handleBackClick = (): void => {
    const crumbs = workspace?.breadcrumbs || []
    if (crumbs.length <= 1) {
      setCurrentFolderId(null)
      return
    }
    // Parent is the root breadcrumb — use null so root workspace loads
    if (crumbs.length === 2) {
      setCurrentFolderId(null)
      return
    }
    setCurrentFolderId(crumbs[crumbs.length - 2].folder_id)
  }

  const getFileIcon = (fileName: string): JSX.Element => {
    const extension = fileName.split('.').pop()?.toLowerCase() || ''
    if (extension === 'pdf') {
      return <FilesPdfIcon className="sp-files-item-icon" aria-hidden />
    }
    return <FilesDocIcon className="sp-files-item-icon" aria-hidden />
  }

  const formatItemDate = (value: string): string => {
    if (!value) return ''
    const parsed = moment(value)
    return parsed.isValid() ? parsed.format('MM-DD-YYYY') : value
  }

  const currentFolderName = useMemo(() => {
    if (!workspace?.breadcrumbs?.length) return ''
    const last = workspace.breadcrumbs[workspace.breadcrumbs.length - 1]
    return formatFolderDisplayName(last.folder_name)
  }, [workspace])

  return (
    <>
      <div className="sp-files">
        <div className="sp-files-section-header">
          <div className="sp-details-section-icon">
            <FilesOverviewIcon
              className="sp-details-section-svg-icon"
              aria-hidden
            />
          </div>
          <span className="sp-files-section-title">Files overview</span>
          <TooltipComponent content="Upload" position="TopCenter">
            <button
              type="button"
              className="sp-files-upload-btn"
              onClick={handleUploadClick}
              aria-label="Upload"
            >
              <FilesUploadIcon className="sp-files-upload-icon" aria-hidden />
            </button>
          </TooltipComponent>
        </div>

        {currentFolderId ? (
          <div className="sp-files-breadcrumb">
            <button
              type="button"
              className="sp-files-breadcrumb-back"
              onClick={handleBackClick}
            >
              <FilesBackIcon
                className="sp-files-breadcrumb-chevron"
                aria-hidden
              />
              <span>Files</span>
            </button>
            <span className="sp-files-breadcrumb-sep" aria-hidden>
              /
            </span>
            <span className="sp-files-breadcrumb-current">
              {currentFolderName}
            </span>
          </div>
        ) : (
          <div className="sp-files-root-title">Files</div>
        )}

        <div className="sp-files-list">
          {isLoading ? (
            <SkeletonList>
              {['55%', '40%', '50%', '35%'].map((width) => (
                <SkeletonCard key={width}>
                  <SkeletonRow>
                    <SkeletonBar width={width} />
                    <SkeletonBar width={16} />
                  </SkeletonRow>
                  <SkeletonRow>
                    <SkeletonBar width="30%" />
                  </SkeletonRow>
                </SkeletonCard>
              ))}
            </SkeletonList>
          ) : currentItems.length === 0 ? (
            <p className="sp-files-empty">No files to display</p>
          ) : (
            currentItems.map((item, index) => (
              <div
                key={item.id}
                className={`sp-files-item${
                  index === currentItems.length - 1
                    ? ' sp-files-item--last'
                    : ''
                }${item.isFolder ? ' sp-files-item--folder' : ''}`}
                onClick={() => item.isFolder && handleFolderClick(item.id)}
                onKeyDown={(e) => {
                  if (item.isFolder && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault()
                    handleFolderClick(item.id)
                  }
                }}
                role={item.isFolder ? 'button' : undefined}
                tabIndex={item.isFolder ? 0 : undefined}
              >
                <div className="sp-files-item-top">
                  {item.isFolder ? (
                    <FilesFolderIcon
                      className="sp-files-item-icon"
                      aria-hidden
                    />
                  ) : (
                    getFileIcon(item.name)
                  )}
                  <div className="sp-files-item-top-main">
                    <span className="sp-files-item-name">{item.name}</span>
                    <span className="sp-files-item-date">
                      {formatItemDate(item.modified)}
                    </span>
                  </div>
                </div>
                <div className="sp-files-item-bottom">
                  <span className="sp-files-item-size">{item.size}</span>
                  <div
                    className="sp-files-item-menu"
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                  >
                    {!item.isFolder &&
                      editDeleteDDComponent(
                        item.name,
                        () => void handleEditFile(item),
                        () => void handleDeleteFile(item),
                      )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      <FilesUploadModal
        show={showUploadModal}
        onHide={handleCloseUploadModal}
        onAttach={(newFiles: FileUpload[]) => {
          const activePatientId = selectedPatient?.patient_id
          if (!activePatientId) return
          const files = newFiles
            .map((item) => item.file)
            .filter((f): f is File => !!f)
          if (files.length === 0) return

          if (shouldUsePatientTrayMockData(activePatientId) && workspace) {
            const uploadedFiles = files.map((file, index) => ({
              file_id: `mock-upload-${Date.now()}-${index}`,
              folder_id: currentFolderId,
              file_name: file.name,
              original_name: file.name,
              file_type: 'document',
              ui_type: file.name.toLowerCase().endsWith('.pdf')
                ? ('PDF' as const)
                : file.type.startsWith('image/')
                  ? ('Image' as const)
                  : ('Document' as const),
              mime_type: file.type || 'application/octet-stream',
              file_size: file.size,
              is_favorite: false,
              download_count: 0,
              last_accessed: null,
              date_time_created_at: new Date().toISOString(),
              created_by: 'mock-user',
              study_uid: null,
              series_uid: null,
              sop_uid: null,
            }))
            setWorkspace({
              ...workspace,
              files: [...workspace.files, ...uploadedFiles],
            })
            setShowUploadModal(false)
            return
          }

          void dispatch(
            uploadFilesAction({
              files,
              patientId: activePatientId,
              folderId: currentFolderId ?? undefined,
            }),
          )
        }}
      />
      <FilesDeleteConfirmModal
        show={showDeleteModal}
        itemName={fileToDelete?.name || ''}
        itemKind={fileToDelete?.isFolder ? 'folder' : 'file'}
        onCancel={() => {
          setShowDeleteModal(false)
          setFileToDelete(null)
        }}
        onDelete={() => void confirmDeleteFile()}
      />

      <Modal
        show={showRenameModal}
        onHide={() => {
          setShowRenameModal(false)
          setFileToRename(null)
          setNewFileName('')
        }}
        centered
        className="rename-file-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Rename file</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <label className="form-label">File name</label>
            <input
              ref={renameInputRef}
              type="text"
              className="form-control"
              placeholder="Enter file name"
              value={newFileName}
              onChange={(e) => setNewFileName(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  handleRenameFile()
                }
              }}
            />
            {fileToRename && (
              <small className="text-muted d-block mt-2">
                Original: {fileToRename.name}
              </small>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            size="md"
            className="button-secondary me-2"
            onClick={() => {
              setShowRenameModal(false)
              setFileToRename(null)
              setNewFileName('')
            }}
          >
            Cancel
          </Button>
          <Button
            size="md"
            className="button-primary"
            onClick={handleRenameFile}
            disabled={
              !newFileName.trim() || newFileName.trim() === fileToRename?.name
            }
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default FilesTab
